import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getBackendUri } from "@/lib/backendSource";
import { isDev } from "@/lib/urlUtils";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { renderName } from "@/lib/personHelpers";
import { Person } from "@/types";

interface DataSyncDialogProps {
  open: boolean;
  onClose: () => void;
  pgaId: string;
}

export default function DataSyncDialog({ open, onClose, pgaId }: DataSyncDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useCurrentUser();

  const isProduction = useMemo(() => !isDev(), []);

  type PersonData = { person: Person };
  const { data: personData } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    skip: !open,
    fetchPolicy: "cache-first"
  });

  const personName = useMemo(() => {
    if (personData?.person) {
      return renderName(
        personData.person.displayName,
        personData.person.informalName,
        personData.person.preferredLastName,
        personData.person.suffixName,
        personData.person.middleName,
        personData.person.firstName
      );
    }
    return null;
  }, [personData]);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const sendSyncRequests = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const backendUri = getBackendUri();
      if (!backendUri) {
        throw new Error("Backend URI not configured");
      }

      const authToken = await getAccessToken();
      if (!authToken) {
        throw new Error("Authentication required. Please log in.");
      }

      const digitsOnly = pgaId.replace(/\D/g, "");

      if (digitsOnly.length !== 8) {
        throw new Error("Invalid PGA ID format. Must be 8 digits.");
      }

      const headers = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      };

      const memberUrl = `${backendUri}/api/servicebus/members/${digitsOnly}`;
      const educationUrl = `${backendUri}/api/servicebus/education/${digitsOnly}`;

      const [memberResponse, educationResponse] = await Promise.all([
        fetch(memberUrl, {
          method: "POST",
          headers
        }),
        fetch(educationUrl, {
          method: "POST",
          headers
        })
      ]);

      if (!memberResponse.ok) {
        let errorMessage = "Member sync failed. Please try again.";
        try {
          const errorData = await memberResponse.json();
          if (errorData.errors) {
            errorMessage = `Member sync failed: ${errorData.errors}`;
          }
        } catch (e) {
          console.error("ServiceBus member sync: Failed to parse error response", e);
        }
        throw new Error(errorMessage);
      }

      if (!educationResponse.ok) {
        let errorMessage = "Education sync failed. Please try again.";
        try {
          const errorData = await educationResponse.json();
          if (errorData.errors) {
            errorMessage = `Education sync failed: ${errorData.errors}`;
          }
        } catch (e) {
          console.error("ServiceBus education sync: Failed to parse error response", e);
        }
        throw new Error(errorMessage);
      }

      await Promise.all([memberResponse.json().catch(() => ({})), educationResponse.json().catch(() => ({}))]);

      showSnackbar("ServiceBus sync requests sent successfully");
      setError(null);
      onClose();
    } catch (error: any) {
      setError(error.message || "Failed to send sync requests. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendRequest = () => {
    sendSyncRequests();
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Data Sync Tool"
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSendRequest}
            disabled={isLoading}
            icon={isLoading ? "progress_activity" : undefined}
            iconClassName={isLoading ? "animate-spin w-6 h-6 !block" : undefined}
          >
            Confirm Sync
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {isProduction ? (
          <div>
            <Banner
              inline
              variant="error"
              description="Production Environment"
              subText="Request will synchronize production data"
            />
          </div>
        ) : (
          <div>
            <Banner
              inline
              variant="info"
              description="Development Environment"
              subText="Request will synchronize development data"
            />
          </div>
        )}

        <div className="text-body-medium">
          Are you sure you want to send sync requests for{" "}
          <strong>{personName ? `${personName} (${pgaId})` : `PGA ID: ${pgaId}`}</strong>? This will trigger both member
          and education data synchronization.
        </div>

        {error && (
          <div>
            <Banner
              inline
              variant="error"
              description="Request Failed"
              subText={error}
              onClose={() => setError(null)}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
